import { KeyboardArrowDownRounded } from "@mui/icons-material";
import { Box, type SxProps, type Theme, Typography } from "@mui/material";
import { FONT_SIZE, ICON_SIZE, INK, MOTION } from "../../../theme/tokens";
import { InHouseTooltip } from "../../InHouseTooltip/InHouseTooltip";
import { TagChip } from "../Badges/TagChip";

/**
 * One value in the cell. `key` must be stable across renders — it is the React key and the test id
 * suffix. `tooltip` is the full already-translated text for a label that can ellipsize at the column's
 * minimum width; omit it for a value that can never truncate (a time range, a short code), where a
 * tooltip repeating what is already visible is only noise.
 */
export type ChipListCellItem = { key: string; label: string; tooltip?: string };

/** Two or three full values beat five ellipsized stubs in a table column; the rest collapse into "+N". */
const DEFAULT_MAX_VISIBLE = 3;

/** Degrees the chevron turns to say "this row's panel is open". */
const OPEN_ROTATION_DEG = 180;

type Props = {
	items: ChipListCellItem[];
	/** Already-translated hint shown while there is nothing configured (e.g. "Not configured"). */
	emptyLabel: string;
	/** How many chips to draw before collapsing the rest into "+N" (default 3). Raise it for a wide column. */
	maxVisible?: number;
	/** Tabular variant — passes through to `TagChip` for values read digit-by-digit (a time range, an id). */
	mono?: boolean;
	/**
	 * The cell opens an editor/detail panel. Draws the chevron affordance — revealed on hover of a real
	 * pointer, while the host grid cell holds keyboard focus, and always while open.
	 */
	expandable?: boolean;
	/** Whether that panel is currently open — pins the chevron open and turns it.  */
	isOpen?: boolean;
	sx?: SxProps<Theme>;
	/** Stamped on the root; each chip derives `-chip-{key}`, the overflow counter `-overflow`. */
	dataTestId?: string;
};

/**
 * A chip list sized for one row of a dense `SystemDataGrid`: the first `maxVisible` values, the rest
 * collapsed into a `+N` whose tooltip names them. Nothing wraps, so every row is the same height —
 * a wrapping multi-value cell clips at the grid's fixed row height and hides values silently.
 *
 * Pair `expandable` with the grid's cell expansion when the full set is edited in a panel below. The
 * chevron is `aria-hidden`: the grid cell, not the glyph, is the control that opens the panel.
 *
 * Caller supplies already-translated strings. See `TagSelectCell` for chips picked from a fixed set.
 */
export const ChipListCell = ({
	items,
	emptyLabel,
	maxVisible = DEFAULT_MAX_VISIBLE,
	mono = false,
	expandable = false,
	isOpen = false,
	sx,
	dataTestId,
}: Props) => {
	const visible = items.slice(0, maxVisible);
	const hidden = items.slice(maxVisible);

	return (
		<Box
			data-testid={dataTestId}
			sx={[
				{
					display: "flex",
					alignItems: "center",
					gap: 0.5,
					width: "100%",
					height: "100%",
					minWidth: 0,
					flexWrap: "nowrap",
					overflow: "hidden",
					// The chevron rests invisible so the values own the row; it fades in for whoever is
					// actually reaching for it. Always drawn while open, or the open row loses its marker.
					"& .chip-list-cell-affordance": {
						opacity: isOpen ? 1 : 0,
						transition: `opacity ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}, transform ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}`,
					},
					// Keyboard parity. A grid moves focus to the CELL, never into this Box, so `:focus-within`
					// here can never match — the rule has to hang off the focused host cell instead.
					'[role="gridcell"]:focus-visible & .chip-list-cell-affordance': { opacity: 1 },
					"@media (hover: hover) and (pointer: fine)": {
						"&:hover .chip-list-cell-affordance": { opacity: 1 },
					},
					// No hover on touch, so the affordance has to be permanent there.
					"@media (hover: none)": {
						"& .chip-list-cell-affordance": { opacity: 1 },
					},
					"@media (prefers-reduced-motion: reduce)": {
						"& .chip-list-cell-affordance": { transition: "none", opacity: 1 },
					},
				},
				...(sx === undefined ? [] : Array.isArray(sx) ? sx : [sx]),
			]}
		>
			{items.length === 0 ? (
				<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, color: INK.DISABLED, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
					{emptyLabel}
				</Typography>
			) : (
				visible.map((item) => (
					<TagChip
						key={item.key}
						label={item.label}
						tooltip={item.tooltip}
						mono={mono}
						sx={{ flex: "0 1 auto" }}
						dataTestId={dataTestId ? `${dataTestId}-chip-${item.key}` : undefined}
					/>
				))
			)}
			{hidden.length > 0 && (
				// Better to name three values fully and count the rest than to ellipsize all of them into
				// unreadable stubs; the full set is one click away in the panel, and here in the tooltip.
				<InHouseTooltip title={hidden.map((item) => item.label).join(", ")}>
					<Box
						component="span"
						data-testid={dataTestId ? `${dataTestId}-overflow` : undefined}
						sx={{ color: INK.MUTED, fontSize: FONT_SIZE.CAPTION, fontWeight: 600, flex: "0 0 auto" }}
					>
						{`+${hidden.length}`}
					</Box>
				</InHouseTooltip>
			)}
			{expandable && (
				<KeyboardArrowDownRounded
					aria-hidden
					className="chip-list-cell-affordance"
					sx={{
						ml: "auto",
						flex: "0 0 auto",
						fontSize: ICON_SIZE.CONTROL,
						color: isOpen ? "primary.main" : INK.MUTED,
						transform: isOpen ? `rotate(${OPEN_ROTATION_DEG}deg)` : "none",
					}}
				/>
			)}
		</Box>
	);
};

export default ChipListCell;
